import { ref, computed, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import type { AmbassadorSignupStep } from '@/components/ambassador-signup/config/ambassador-signup-flow-config';
import {
  getStepConfig,
  getNextStep,
  getFullTouchpointUrl,
  autoPopulateTouchpoints,
} from '@/components/ambassador-signup/config/ambassador-signup-flow-config';
import { useAmbassadorSignupApi } from './useAmbassadorSignupApi';
import { useResendTimer } from '@/composables/useResendTimer';
import type { AmbassadorSignupFormData } from '@/types/IAmbassadorSignup';
import { useUserStore } from '@/stores/user';
import { readJwtToken } from '@/services/auth-tokens';
import { matchesZodEmail } from '@/utils/emailValidation';

function _getRedirectionName(
  redirectAfterSignUpTo: AmbassadorSignupFormData['redirectAfterSignUpTo']
) {
  if (redirectAfterSignUpTo === 'challenge') {
    return 'challenges';
  }
  if (redirectAfterSignUpTo === 'event') {
    return 'events';
  }
  if (redirectAfterSignUpTo === 'ugc') {
    return 'ugc';
  }
  if (redirectAfterSignUpTo === 'campaign') {
    return 'helper-status';
  }
  return 'relations';
}

// Step management composable
export function useAmbassadorSignupFlow() {
  const router = useRouter();
  const route = useRoute();
  const { t, locale } = useI18n({ useScope: 'global' });

  // Get route parameters for campaign/teamspace detection
  const campaignUuid = computed(() => route.params.campaignUuid as string);
  const teamspaceUuid = computed(() => route.params.teamspaceUuid as string);
  const isTeamspacePath = computed(() => route.path.includes('/tmspc/'));

  // API hooks for new endpoints
  const {
    usePublicCampaignQuery,
    useProtectedCampaignQuery,
    usePublicTeamspaceQuery,
    useProtectedTeamspaceQuery,
    useUpdateLanguageMutation,
    useRegisterHelperByEmailMutation,
    useManagePhoneNumberMutation,
    useVerifyPhoneNumberCodeMutation,
    useUpdateNameMutation,
    useUpdateProfilePictureMutation,
    useUpdatePositionLanguagesMutation,
    useUpdateAddressMutation,
    useUpdateActionsInterestedInMutation,
    useFillTouchPointsMutation,
  } = useAmbassadorSignupApi();

  // Determine which data to fetch based on route and authentication status
  const userStore = useUserStore();

  const isAuthenticated = computed(() => {
    try {
      // First, check for JWT token existence
      const jwtToken = readJwtToken('user', true);

      return !!jwtToken && !!userStore.user && !!userStore.user?.id;
    } catch (error) {
      console.warn('🔐 Error checking authentication status:', error);
      return false;
    }
  });

  // Campaign data queries
  const publicCampaignQuery = usePublicCampaignQuery(
    campaignUuid.value,
    !isTeamspacePath.value && !!campaignUuid.value && !isAuthenticated.value
  );
  const protectedCampaignQuery = useProtectedCampaignQuery(
    campaignUuid.value,
    !isTeamspacePath.value && !!campaignUuid.value && isAuthenticated.value
  );

  // Teamspace data queries
  const publicTeamspaceQuery = usePublicTeamspaceQuery(
    teamspaceUuid.value,
    isTeamspacePath.value && !!teamspaceUuid.value && !isAuthenticated.value
  );
  const protectedTeamspaceQuery = useProtectedTeamspaceQuery(
    teamspaceUuid.value,
    isTeamspacePath.value && !!teamspaceUuid.value && isAuthenticated.value
  );

  // Mutations
  const registerEmailMutation = useRegisterHelperByEmailMutation();
  const managePhoneMutation = useManagePhoneNumberMutation();
  const verifyPhoneMutation = useVerifyPhoneNumberCodeMutation();
  const updateLanguageMutation = useUpdateLanguageMutation();
  const updateNameMutation = useUpdateNameMutation();
  const updateProfilePictureMutation = useUpdateProfilePictureMutation();
  const updatePositionMutation = useUpdatePositionLanguagesMutation();
  const fillTouchPointsMutation = useFillTouchPointsMutation();
  const updateAddressMutation = useUpdateAddressMutation();
  const updateActionsInterestedInMutation =
    useUpdateActionsInterestedInMutation();

  // Phone verification resend timer - shared across components
  const {canResend: canSendPhoneCode,
    startTimer: startPhoneTimer,
    resetTimer: resetPhoneTimer} = useResendTimer();

  // Computed data accessor based on current path
  const currentData = computed(() => {
    if (isTeamspacePath.value) {
      return isAuthenticated.value
        ? protectedTeamspaceQuery.data.value
        : publicTeamspaceQuery.data.value;
    } else {
      return isAuthenticated.value
        ? protectedCampaignQuery.data.value
        : publicCampaignQuery.data.value;
    }
  });

  // Loading state
  const isLoading = computed(() => {
    const queryLoading = isTeamspacePath.value
      ? isAuthenticated.value
        ? protectedTeamspaceQuery.isLoading.value
        : publicTeamspaceQuery.isLoading.value
      : isAuthenticated.value
        ? protectedCampaignQuery.isLoading.value
        : publicCampaignQuery.isLoading.value;

    const mutationLoading =
      registerEmailMutation.isPending.value ||
      managePhoneMutation.isPending.value ||
      verifyPhoneMutation.isPending.value ||
      updateNameMutation.isPending.value ||
      updateProfilePictureMutation.isPending.value ||
      updatePositionMutation.isPending.value ||
      fillTouchPointsMutation.isPending.value;

    return queryLoading || mutationLoading;
  });

  // Current step state
  const currentStep = ref<AmbassadorSignupStep>('welcome');
  const disableNext = ref(false);
  const codeValidationResult = ref<{ [key: string]: string }>({});

  // Simple step history - tracks navigation path (in-memory only)
  // Excludes verification-phone step since users shouldn't return to it directly
  const stepHistory = ref<AmbassadorSignupStep[]>([]);

  // Add current step to history when navigating TO a new step
  const addToHistory = (step: AmbassadorSignupStep) => {
    // Don't add verification-phone to history
    if (step !== 'verification-phone') {
      stepHistory.value.push(step);
    }
  };

  // Get previous step and remove current from history
  const goBackFromHistory = (): AmbassadorSignupStep | null => {
    if (stepHistory.value.length === 0) {
      return null;
    }

    const lastStep = stepHistory.value[stepHistory.value.length - 1];

    if (currentStep.value) {
      stepHistory.value.pop(); // Remove current step
    }

    return lastStep; // Return previous step
  };

  // Form data with persistence
  const defaultFormData: AmbassadorSignupFormData = {
    redirectAfterSignUpTo: '',
    nextStep: 'language',
    hasAlreadyStarted: false,
    ambassadorUuid: '',
    teamspaceUuid: '',
    organizationName: '',
    organizationLogoUrl: '',
    organizationTermsUrl: '',
    helperProfilePictures: [],
    language: (locale.value as 'en' | 'fr') || 'en',
    isLanguageSetByUser: false,
    email: '',
    termsAccepted: false,
    isEmailVerified: false,
    phoneNumber: '',
    isPhoneVerified: false,
    firstname: '',
    lastname: '',
    profilePictureUrl: '',
    profilePictureUrlMedium: '',
    position: '',
    languagesSpoken: [],
    address: '',
    googlePlaceId: '',
    actionsInterestedIn: [
      'matching',
      'ugc',
      'event',
      'challenge',
      'referral',
      'review',
    ],
    userTouchPoints: [],
    touchPoints: [],

    profilePicture: null,
    code: '',
    turnstileToken: undefined,
  };

  const formData = ref<AmbassadorSignupFormData>(defaultFormData);

  // Step configuration
  const currentStepConfig = computed(() => getStepConfig(currentStep.value));

  // Navigation methods
  const goBack = () => {
    // Check both canGoBack config and if we have history to go back to
    if (!currentStepConfig.value?.canGoBack || stepHistory.value.length === 0) {
      return;
    }

    const previousStep = goBackFromHistory();
    if (previousStep) {
      currentStep.value = previousStep;
    }
  };

  const goForward = async () => {
    await handleStepForward();
  };

  // Validation for current step
  const canProceed = computed(() => {
    switch (currentStep.value) {
      case 'language':
        return formData.value.language !== '';
      case 'email': {
        const email = formData.value.email;
        if (!email || email.trim() === '') return false;
        return matchesZodEmail(email) && formData.value.termsAccepted;
      }
      case 'phone':
        return formData.value.phoneNumber !== '';
      case 'verification-phone':
        return formData.value.code !== '' && formData.value.code.length === 6;
      case 'name':
        return (
          formData.value.firstname !== '' && formData.value.lastname !== ''
        );
      case 'profile':
        return true; // Bio and profile picture are optional
      case 'address':
        return true; // Address is optional
      case 'actionsInterestedIn':
        return formData.value.actionsInterestedIn.length > 0;
      case 'touchPoints':
        return (
          formData.value.userTouchPoints.length > 0 &&
          formData.value.userTouchPoints.every((tp) => tp.value.trim() !== '')
        );
      default:
        return true;
    }
  });

  // Determine if back button should be visible
  const canShowBackButton = computed(() => {
    // Must have canGoBack set to true AND have history to go back to
    return (
      currentStepConfig.value?.canGoBack === true &&
      stepHistory.value.length > 0
    );
  });

  // Step-specific forward navigation logic
  const handleStepForward = async () => {
    if (disableNext.value) {
      return;
    }

    disableNext.value = true;
    codeValidationResult.value = {};

    // Add current step to history before navigating away (unless it's verification-phone)
    // Add to history
    addToHistory(currentStep.value);

    try {
      switch (currentStep.value) {
        case 'welcome': {
          // Just navigate to next step, no API call needed
          if (formData.value.hasAlreadyStarted) {
            currentStep.value =
              formData.value.nextStep !== 'completed'
                ? formData.value.nextStep
                : 'language';
          } else {
            currentStep.value =
              getNextStep(currentStep.value) || currentStep.value;
          }
          break;
        }

        case 'start': {
          // Just navigate to next step, no API call needed
          currentStep.value =
            getNextStep(currentStep.value) || currentStep.value;
          break;
        }

        case 'share': {
          // Handle share step navigation logic (check if language already set)
          currentStep.value =
            formData.value.nextStep !== 'completed'
              ? formData.value.nextStep
              : 'language';
          break;
        }
        case 'language':
          // Call update-language API when leaving language step
          await handleLanguageStep();
          break;

        case 'email':
          await handleEmailStep();
          break;

        case 'phone':
          await handlePhoneStep();
          break;

        case 'verification-phone':
          await handleVerificationStep();
          break;

        case 'name':
          await handleNameStep();
          break;

        case 'profile':
          await handleProfileStep();
          break;

        case 'address':
          await handleAddressStep();
          break;

        case 'actionsInterestedIn':
          await handleActionsInterestedInStep();
          break;

        case 'touchPoints':
          await handleTouchpointsStep();
          break;

        case 'resume':
          await handleResumeStep();
          break;

        default:
          currentStep.value =
            getNextStep(currentStep.value) || currentStep.value;
      }
    } catch (error) {
      console.error('Error in step forward:', error);

      // Handle error appropriately
      if (currentStep.value === 'verification-phone') {
        codeValidationResult.value = {
          code: t('helpeeCampaignSignUpView.codeWrong'),
        };
      }
    } finally {
      disableNext.value = false;
    }
  };

  const handleLanguageStep = async () => {
    if (!formData.value.language) {
      const nextStep = getNextStep(currentStep.value);
      currentStep.value = nextStep || currentStep.value;
      return;
    }

    // Special logic for language endpoint:
    // 1. If ambassadorUuid exists → Send language API call now
    // 2. If no ambassadorUuid → Store language and send it later at email step

    if (formData.value.ambassadorUuid) {
      await updateLanguageMutation.mutateAsync({
        language: formData.value.language as 'fr' | 'en',
      });
    }

    // Always mark as user-set when user completes the language step
    formData.value.isLanguageSetByUser = true;
    formData.value.languagesSpoken = [formData.value.language as 'fr' | 'en'];

    const nextStep = getNextStep(currentStep.value);
    currentStep.value = nextStep || currentStep.value;
  };

  // Step handlers
  const handleEmailStep = async () => {
    try {
      // Register email with language (language is always sent with email)
      const result = await registerEmailMutation.mutateAsync({
        email: formData.value.email,
        teamspaceUuid: formData.value.teamspaceUuid,
        source: (route.query.source as string) || '',
        language: formData.value.language as 'fr' | 'en',
        turnstileToken: formData.value.turnstileToken,
      });

      if (result.redirectTo === 'confirm-email') {
        currentStep.value = 'confirm';
      } else if (result.redirectTo === 'login') {
        currentStep.value = 'alreadyExist';
      } else {
        throw new Error('Unexpected redirectTo value');
      }
    } catch (error) {
      formData.value.turnstileToken = undefined;
      throw error;
    }
  };

  const handlePhoneStep = async () => {
    // Check if user is authenticated and has verified phone
    if (currentData?.value?.isPhoneVerified) {
      currentStep.value = 'name';
      return;
    }

    if (!formData.value.phoneNumber) {
      throw new Error('Phone number is required');
    }

    // Only send code if timer allows (prevents rapid retries when going back/forward)
    // If timer is still running, just navigate to verification step
    if (canSendPhoneCode.value) {
      await managePhoneMutation.mutateAsync({
        phoneNumber: formData.value.phoneNumber,
      });
      // Start the timer after successfully sending the code
      startPhoneTimer();
    }

    currentStep.value = 'verification-phone';

    // Force reactivity update
    await nextTick();
  };

  const handleVerificationStep = async () => {
    const result = await verifyPhoneMutation.mutateAsync({
      code: formData.value.code,
    });
    if (result.message === 'ok') {
      // Reset timer when verification is successful
      resetPhoneTimer();
      currentStep.value = 'name';
    }
  };

  const handleNameStep = async () => {
    if (!formData.value.firstname || !formData.value.lastname) {
      throw new Error('First name and last name are required');
    }
    const result = await updateNameMutation.mutateAsync({
      firstname: formData.value.firstname,
      lastname: formData.value.lastname,
    });

    if (result && result.profilePictureUrl) {
      formData.value.profilePictureUrl = result.profilePictureUrl;
      formData.value.profilePictureUrlMedium = result.profilePictureUrlMedium;
    }

    currentStep.value = 'profile';
  };

  // Handle immediate profile picture upload when user selects a file
  const handleProfilePictureUpload = async (file: File) => {
    try {
      const upload = await updateProfilePictureMutation.mutateAsync({
        profilePicture: file,
      });

      if (upload && upload.profilePictureUrl) {
        formData.value.profilePictureUrl = upload.profilePictureUrl;
        formData.value.profilePictureUrlMedium = upload.profilePictureUrlMedium;
        formData.value.profilePicture = file; // Keep the file reference for form state
      }
      return upload;
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      throw error;
    }
  };

  const handleProfileStep = async () => {
    if (formData.value.position || formData.value.languagesSpoken.length > 0) {
      await updatePositionMutation.mutateAsync({
        position: formData.value.position || '',
        languagesSpoken: formData.value.languagesSpoken,
      });
    }

    // Profile picture is now handled immediately when uploaded via handleProfilePictureUpload
    currentStep.value = 'address';
  };

  const handleAddressStep = async () => {
    if (formData.value.address) {
      const trimmedPlaceId = formData.value.googlePlaceId?.trim();
      const response = await updateAddressMutation.mutateAsync({
        address: formData.value.address,
        ...(trimmedPlaceId ? { googlePlaceId: trimmedPlaceId } : {}),
      });

      if (response?.address) {
        formData.value.address = response.address;
      }
      if (response?.googlePlaceId !== undefined) {
        formData.value.googlePlaceId = response.googlePlaceId || '';
      }
    }

    currentStep.value = 'actionsInterestedIn';
  };

  const handleActionsInterestedInStep = async () => {
    if (formData.value.actionsInterestedIn.length > 0) {
      await updateActionsInterestedInMutation.mutateAsync({
        actionsInterestedIn: formData.value.actionsInterestedIn,
      });
    }

    // Auto-populate touchpoints before navigating to touchPoints step
    const autoPopulatedTouchPoints = autoPopulateTouchpoints(
      formData.value.email,
      formData.value.phoneNumber || '',
      formData.value.userTouchPoints
    );

    if (autoPopulatedTouchPoints.length > 0) {
      formData.value.userTouchPoints = autoPopulatedTouchPoints;
      // Force Vue reactivity
      await nextTick();
    }

    currentStep.value = 'touchPoints';
  };

  const handleTouchpointsStep = async () => {
    const userTouchPoints = formData.value.userTouchPoints.map(
      (userTouchPoint) => {
        // Try to find touchpoint in form data first, then fallback to temp data
        const touchPoint = formData.value.touchPoints.find(
          (tp) => tp.id === userTouchPoint.touchPointId
        );
        if (!touchPoint) {
          console.warn(
            'Touchpoint not found for ID:',
            userTouchPoint.touchPointId
          );
        }

        return {
          touchPointId: userTouchPoint.touchPointId,
          value: getFullTouchpointUrl(
            touchPoint?.name || '',
            userTouchPoint.value
          ),
        };
      }
    );

    await fillTouchPointsMutation.mutateAsync({
      userTouchPoints: userTouchPoints || [],
    });

    currentStep.value = 'resume';
    return;
  };

  const handleResumeStep = async () => {
    // Guard: only execute if we're actually on the resume step
    if (currentStep.value !== 'resume') {
      return;
    }

    // Navigate to the main app by reloading
    const redirectName = _getRedirectionName(
      formData.value.redirectAfterSignUpTo
    );

    // Get the route URL and navigate with page reload
    const routeUrl = router.resolve({ name: redirectName }).href;
    window.location.href = routeUrl;

    // Clear step history on successful completion
    stepHistory.value = [];
  };

  // Initialize with campaign/teamspace data when available
  const initializeWithData = (data: typeof currentData.value) => {
    if (!data) {
      return;
    }

    if (data.language && data.language !== locale.value) {
      locale.value = data.language as 'en' | 'fr';
    }

    // 1. Capture current uploaded profile picture URLs before overwriting
    const currentUploadedUrls = {
      profilePictureUrl: formData.value.profilePictureUrl,
      profilePictureUrlMedium: formData.value.profilePictureUrlMedium,
    };

    const hasUploadedProfilePicture =
      currentUploadedUrls.profilePictureUrl &&
      currentUploadedUrls.profilePictureUrl !== data.profilePictureUrl;

    // 2. Apply API data to formData
    formData.value = {
      ...formData.value,
      ...data,
    };

    // 3. If we had uploaded a new profile picture, restore those URLs
    if (hasUploadedProfilePicture) {
      formData.value.profilePictureUrl = currentUploadedUrls.profilePictureUrl;
      if (currentUploadedUrls.profilePictureUrlMedium) {
        formData.value.profilePictureUrlMedium =
          currentUploadedUrls.profilePictureUrlMedium;
      }
    }

    // 2. Handle redirects if signup already completed
    if (data.nextStep === 'completed') {
      const redirectName = _getRedirectionName(data.redirectAfterSignUpTo);
      // Get the route URL and navigate with page reload
      const routeUrl = router.resolve({ name: redirectName }).href;
      window.location.href = routeUrl;
      return;
    }

    // 3. Set initial step based on progress
    if (data.hasAlreadyStarted && data.nextStep) {
      currentStep.value = data.nextStep;

      // Auto-populate touchpoints if we're starting on the touchPoints step
      if (data.nextStep === 'touchPoints') {
        const autoPopulatedTouchPoints = autoPopulateTouchpoints(
          formData.value.email,
          formData.value.phoneNumber || '',
          formData.value.userTouchPoints
        );

        if (autoPopulatedTouchPoints.length > 0) {
          formData.value.userTouchPoints = autoPopulatedTouchPoints;
        }
      }
    }
  };

  // Watch for API data changes (without immediate to avoid empty data calls)
  watch(currentData, (newData) => {
    if (newData) {
      initializeWithData(newData);
    }
  });

  // Separate watcher for setting UUIDs from route (this can be immediate)
  watch(
    [teamspaceUuid, isTeamspacePath],
    ([newTeamspaceUuid, isTeamspace]) => {
      if (newTeamspaceUuid && isTeamspace) {
        formData.value.teamspaceUuid = newTeamspaceUuid;
      }
    },
    { immediate: true }
  );

  // Clear validation errors when entering new steps
  // This prevents validation errors from showing before user starts typing
  watch(currentStep, (newStep, oldStep) => {
    if (newStep !== oldStep) {
      // Clear code validation results when entering any new step
      if (newStep === 'verification-phone' || newStep === 'email') {
        codeValidationResult.value = {};
      }

      // Auto-populate touchpoints when entering touchPoints step
      if (newStep === 'touchPoints') {
        const autoPopulatedTouchPoints = autoPopulateTouchpoints(
          formData.value.email,
          formData.value.phoneNumber || '',
          formData.value.userTouchPoints
        );

        if (autoPopulatedTouchPoints.length > 0) {
          formData.value.userTouchPoints = autoPopulatedTouchPoints;
        }
      }
    }
  });

  // Reset form data
  const resetForm = () => {
    formData.value = { ...defaultFormData };
    stepHistory.value = [];
    currentStep.value = 'welcome';
  };

  return {
    // State
    currentStep,
    formData,
    disableNext,
    codeValidationResult,
    currentStepConfig,
    canProceed,
    canShowBackButton,

    // Navigation
    goBack,
    goForward,

    // Utilities
    resetForm,
    handleProfilePictureUpload, // Export for immediate profile picture upload

    // API integration // Read-only API data for display purposes
    currentData: currentData, // Read-only API data for display purposes
    isLoading,
    isCampaignLoading: isLoading,
  };
}
